import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { ChartDocument } from "@/types/chart";

// Per-window chart bodies, keyed by chartDocId (TODO §15/§3.3). A mirror of `ganttSlice`,
// and for the same reason: the whole document is rewritten on every keystroke in the data
// table, and folding that into `DeckDocument` would rewrite the entire deck record per cell.
//
// The one place this differs from the three body slices before it is downstream, not here:
// §3.3.3 debounces the PERSISTENCE effect ~300ms. A grid, a diagram and a schedule all arrive
// pre-debounced from the library that owns them; a table we built ourselves dispatches per
// keystroke. Debouncing in the listener rather than in the reducer keeps Redux instant, which
// is what makes the chart redraw as the user types.
export type ChartState = Record<string, ChartDocument>;

const initialState: ChartState = {};

const chartSlice = createSlice({
  name: "chart",
  initialState,
  reducers: {
    // §17.4.5 MERGES a map in, rather than replacing it — the nugget path's reducer. Inserting
    // a nugget brings its charts into a session already holding this deck's, so `hydrated`
    // (which replaces) would wipe the deck.
    //
    // Deliberately NOT in the persistence listener's matcher, for the reason
    // `ganttDocsMerged` is not: these bodies were just read under the nugget's owner key, and
    // writing them back under this deck's is the exact fork §17.1.5 exists to prevent.
    chartDocsMerged(state, action: PayloadAction<ChartState>) {
      Object.assign(state, action.payload);
    },
    // Replaces the whole map on IndexedDB hydration.
    hydrated(_state, action: PayloadAction<ChartState>) {
      return action.payload;
    },
    chartDocCreated(
      state,
      action: PayloadAction<{ docId: string; doc: ChartDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    chartDocUpdated(
      state,
      action: PayloadAction<{ docId: string; doc: ChartDocument }>,
    ) {
      state[action.payload.docId] = action.payload.doc;
    },
    // Unlinks the body from this session. Whether the IndexedDB row is actually DELETED is
    // answered in the persistence listener against the row's owner key (§3.3.4 / §17.6.2),
    // exactly as it is for diagrams and gantts.
    chartDocRemoved(state, action: PayloadAction<{ docId: string }>) {
      delete state[action.payload.docId];
    },
  },
});

export const {
  hydrated,
  chartDocsMerged,
  chartDocCreated,
  chartDocUpdated,
  chartDocRemoved,
} = chartSlice.actions;

export default chartSlice.reducer;
